import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ChatList from './ChatList';
import ChatRoom from './Chatroom';
import MainNavbar from '../MainNavbar';
import SubscriptionModal from '../Pages/SubscriptionModal';
import './ChatPage.css';
import { useNavigate } from 'react-router-dom';
import { useContext } from "react";
import AlertContext from "../../../contexts/AlertContext";

const ChatPage = ({ userId }) => {
  /* ───────── state ───────── */
  const [currentUser, setCurrentUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { showAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  /* ───────── fetch current user ───────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `https://back.varankal.com/user/currentregister-details/${userId}`,
          { withCredentials: true }
        );
        setCurrentUser(data);
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    })();
  }, [userId]);

  /* ───────── fetch unread counts ───────── */
  useEffect(() => {
    if (!currentUser?.userId) return;

    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(
          `https://back.varankal.com/chats/unread-counts/${currentUser.userId}`
        );
        setUnreadCounts(data);            // { senderId: count }
      } catch (err) {
        console.error('Failed to fetch unread counts:', err);
      }
    };

    fetchUnread();
  }, [currentUser?.userId]);

  /* ───────── fetch profile list + last‑message info + requestStatus ───────── */
  useEffect(() => {
    if (!currentUser?.userId) return;

    const fetchProfiles = async () => {
      try {
        const [registerRes, chattedRes] = await Promise.all([
          axios.get('https://back.varankal.com/user/all-register-details', { withCredentials: true }),
          axios.get(`https://back.varankal.com/chats/chattedUsers/${currentUser.userId}`)
        ]);

        /* ---------- basic user data ---------- */
        const registerData = registerRes.data.map(p => ({
          userId: p.userId,
          name: p.basicDetails.name,
          gender: p.basicDetails.gender,
          profilePicture: p.basicDetails.profilePicture
        }));

        /* ---------- keep only opposite gender ---------- */
        const opposite = registerData.filter(
          p => p.gender !== currentUser.basicDetails.gender
        );

        /* ---------- map of lastMessageTime by user ---------- */
        const chattedMap = {};
        chattedRes.data.forEach(u => {
          chattedMap[u.userId] = u.lastMessageTime;
        });

        /* ---------- merge requestStatus + lastMessageTime ---------- */
        const mergedUsers = await Promise.all(
          opposite.map(async u => {
            const requestStatus = await axios
              .get(
                `https://back.varankal.com/user/request-status/${currentUser.userId}/${u.userId}`,
                { withCredentials: true }
              )
              .then(r => r.data.status)
              .catch(() => null);

            return {
              ...u,
              requestStatus,
              lastMessageTime: chattedMap[u.userId] || null
            };
          })
        );

        /* ---------- sort: newest chat first ---------- */
        mergedUsers.sort((a, b) => {
          if (a.lastMessageTime && b.lastMessageTime) {
            return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
          }
          if (a.lastMessageTime) return -1;
          if (b.lastMessageTime) return 1;
          return 0;
        });

        setAllUsers(mergedUsers);
      } catch (err) {
        console.error('Error fetching profiles:', err);
      }
    };

    fetchProfiles();
  }, [currentUser?.userId]);

  /* ───────── open chat: clear local badge immediately ───────── */
  // const handleUserClick = user => {
  //   setUnreadCounts(prev => ({ ...prev, [String(user.userId)]: 0 }));
  //   setSelectedUser(user);
  // };
  const handleUserClick = async (profile) => {
    const userId = profile.userId;

    if (!userId) {
      showAlert("Invalid profile ID. Cannot start chat.");
      return;
    }

    try {
      const response = await axios.get(`https://back.varankal.com/user/view-profile/${userId}`, {
        withCredentials: true,
      });

      // ✅ If plan is valid, allow chat
      if (response.data.profile) {
        setUnreadCounts(prev => ({ ...prev, [String(userId)]: 0 }));
        setSelectedUser(profile);
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        showAlert("Upgrade your plan to chat.");
      } else {
        showAlert("An error occurred while trying to start the chat.");
      }
    }
  };

  /* ───────── modal helper ───────── */
  const handleSubscribe = () => {
    setShowModal(false);
    navigate('/subscription');
  };

  /* ───────── render ───────── */
  return (
    <>
      <MainNavbar />

      <div className="ChatPage00">
        <div className="chat-page">
          {/* ---------- left list ---------- */}
          <div className="chat-list-container">
            <ChatList
              users={allUsers}
              unreadCounts={unreadCounts}
              onUserClick={handleUserClick}
            />
          </div>

          {/* ---------- right room ---------- */}
          {selectedUser && (
            <ChatRoom
              currentUser={currentUser}
              selectedProfile={selectedUser}
              onClose={() => setSelectedUser(null)}
            />
          )}
        </div>
      </div>

      <SubscriptionModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubscribe={handleSubscribe}
      />
    </>
  );
};

export default ChatPage;
